import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Building2, Globe, Key, Save, Loader2, 
  CheckCircle, Copy, AlertCircle, CreditCard, Mail,
  DollarSign, Send, Bell, Shield, Volume2, Zap, ExternalLink, Users, Code, Eye, IdCard, Database
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AddressInput from '@/components/ui/AddressInput';
import ClinicGoogleOAuthSettings from './ClinicGoogleOAuthSettings';
import NotificationSettings from '@/components/settings/NotificationSettings';
import PatientPortalLinks from './PatientPortalLinks';
import EmbedCodeGenerator from './EmbedCodeGenerator';
import ImpersonationSettings from './ImpersonationSettings';
import PatientIdSettings from './PatientIdSettings';
import PaymentMethodsSettings from './PaymentMethodsSettings';
import DataManagement from './DataManagement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClinicSettingsPage = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clinic, setClinic] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    subdomain: '',
    slug: '',
    custom_domain: '',
    primary_color: '#14b8a6',
    secondary_color: '#0d9488',
    google_maps_api_key: ''
  });

  // Billing Settings State
  const [billingSettings, setBillingSettings] = useState({
    stripe: { publishable_key: '', secret_key: '', webhook_secret: '', test_mode: true, enabled: false },
    paypal: { client_id: '', client_secret: '', sandbox_mode: true, enabled: false },
    square: { application_id: '', access_token: '', location_id: '', sandbox_mode: true, enabled: false },
    payment: { currency: 'USD', tax_rate: 0, accept_tips: false, minimum_charge: 0 },
    invoice: { invoice_prefix: 'INV-', payment_terms_days: 30, auto_send_invoices: true, include_clinic_logo: true }
  });

  // Email Settings State
  const [emailSettings, setEmailSettings] = useState({
    provider: 'sendgrid',
    sendgrid: { api_key: '', from_email: '', from_name: '', enabled: false },
    smtp: { host: '', port: 587, username: '', password: '', from_email: '', from_name: '', use_tls: true, enabled: false },
    templates: { logo_url: '', primary_color: '#14b8a6', footer_text: '', include_social_links: true },
    notifications: { send_appointment_reminders: true, reminder_hours_before: 24, send_membership_confirmations: true, send_payment_receipts: true, send_welcome_emails: true },
    is_verified: false
  });

  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [clinicFeatures, setClinicFeatures] = useState({});

  useEffect(() => {
    fetchClinicData();
    fetchBillingSettings();
    fetchEmailSettings();
    fetchClinicFeatures();
  }, []);

  const fetchClinicFeatures = async () => {
    try {
      const response = await axios.get(`${API}/clinic/subscription-features`);
      setClinicFeatures(response.data.features || {});
    } catch (error) {
      console.error('Failed to fetch clinic features:', error);
      // Default to empty - will show upgrade message
      setClinicFeatures({});
    }
  };

  // Check if clinic has Custom Email Domain feature
  const hasCustomEmailDomain = clinicFeatures.custom_email_domain === true;

  const fetchBillingSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clinic/billing-settings`);
      
      // Also fetch Clover settings
      try {
        const cloverRes = await axios.get(`${API}/clover/config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBillingSettings({
          ...response.data,
          clover: cloverRes.data
        });
      } catch (cloverError) {
        // Clover not configured, just use regular billing settings
        setBillingSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch billing settings:', error);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API}/clinic/email-settings`);
      setEmailSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch email settings:', error);
    }
  };

  const fetchClinicData = async () => {
    try {
      // Get current user's clinic info
      const response = await axios.get(`${API}/clinic/info`);
      setClinic(response.data);
      
      setFormData({
        name: response.data.name || '',
        contact_email: response.data.contact_email || '',
        contact_phone: response.data.contact_phone || '',
        address: response.data.address || '',
        subdomain: response.data.subdomain || '',
        slug: response.data.slug || '',
        custom_domain: response.data.custom_domain || '',
        primary_color: response.data.settings?.primary_color || '#14b8a6',
        secondary_color: response.data.settings?.secondary_color || '#0d9488',
        google_maps_api_key: response.data.settings?.google_maps_api_key || ''
      });
    } catch (error) {
      console.error('Failed to fetch clinic data:', error);
      toast.error('Failed to load clinic settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(`${API}/clinic/info`, {
        name: formData.name,
        subdomain: formData.subdomain,
        slug: formData.slug,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        address: formData.address
      });
      
      toast.success('General settings saved successfully!');
      fetchClinicData();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Clinic Settings</h1>
        <p className="text-gray-600 mt-2">Manage your clinic profile and configuration</p>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="general">
            <Building2 className="h-4 w-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="auth">
            <Shield className="h-4 w-4 mr-2" />
            Authentication
          </TabsTrigger>
          <TabsTrigger value="billing">
            <CreditCard className="h-4 w-4 mr-2" />
            Billing
          </TabsTrigger>
          <TabsTrigger value="email">
            <Mail className="h-4 w-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="wordpress">
            <Globe className="h-4 w-4 mr-2" />
            WordPress
          </TabsTrigger>
          <TabsTrigger value="embed">
            <Code className="h-4 w-4 mr-2" />
            Embed Widget
          </TabsTrigger>
          <TabsTrigger value="integrations">
            <Key className="h-4 w-4 mr-2" />
            API & Integrations
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <Volume2 className="h-4 w-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="patient-portal">
            <Users className="h-4 w-4 mr-2" />
            Patient Portal
          </TabsTrigger>
          <TabsTrigger value="patient-id">
            <IdCard className="h-4 w-4 mr-2" />
            Patient ID
          </TabsTrigger>
          <TabsTrigger value="payment-methods">
            <CreditCard className="h-4 w-4 mr-2" />
            Payment Methods
          </TabsTrigger>
          <TabsTrigger value="impersonation">
            <Eye className="h-4 w-4 mr-2" />
            Patient View
          </TabsTrigger>
          <TabsTrigger value="data-management">
            <Database className="h-4 w-4 mr-2" />
            Data Management
          </TabsTrigger>
        </TabsList>

        {/* General Settings Tab */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>General Information</CardTitle>
              <CardDescription>
                Update your clinic's basic information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveGeneral} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Clinic Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Subdomain Field */}
                <div className="space-y-2">
                  <Label htmlFor="subdomain" className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-teal-600" />
                    Clinic Subdomain *
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="subdomain"
                      name="subdomain"
                      value={formData.subdomain}
                      onChange={(e) => {
                        // Only allow lowercase letters, numbers, and hyphens
                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                        setFormData(prev => ({ ...prev, subdomain: value }));
                      }}
                      placeholder="your-clinic-name"
                      className="font-mono"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Used in your patient portal URL. Only lowercase letters, numbers, and hyphens allowed.
                  </p>
                </div>

                {/* Widget Slug Field */}
                <div className="space-y-2">
                  <Label htmlFor="slug" className="flex items-center gap-2">
                    <Code className="h-4 w-4 text-purple-600" />
                    Widget URL Slug
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="slug"
                      name="slug"
                      value={formData.slug}
                      onChange={(e) => {
                        // Only allow lowercase letters, numbers, and hyphens
                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                        setFormData(prev => ({ ...prev, slug: value }));
                      }}
                      placeholder="my-clinic"
                      className="font-mono"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Used in embed widget URLs. Example: <code className="bg-gray-100 px-1 rounded">mymedmember.com/api/embed/widget/<strong>{formData.slug || 'your-slug'}</strong>.js</code>
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Contact Email *</Label>
                    <Input
                      id="contact_email"
                      name="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact_phone">Phone Number</Label>
                    <Input
                      id="contact_phone"
                      name="contact_phone"
                      type="tel"
                      value={formData.contact_phone}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <AddressInput
                  label="Clinic Address"
                  value={formData.address}
                  onChange={handleChange}
                  onAddressSelected={(data) => {
                    setFormData(prev => ({
                      ...prev,
                      address: data.formatted_address
                    }));
                  }}
                  multiline
                />

                <div className="space-y-2">
                  <Label>Subscription Status</Label>
                  <div className="flex items-center gap-3">
                    <Badge className={`${
                      clinic.status === 'trial' ? 'bg-blue-500' :
                      clinic.status === 'active' ? 'bg-green-500' :
                      'bg-gray-500'
                    }`}>
                      {(clinic.status || 'unknown').toUpperCase()}
                    </Badge>
                    <span className="text-sm text-gray-600">
                      {clinic.subscription_tier} Plan
                    </span>
                  </div>
                  {clinic.trial_ends_at && clinic.status === 'trial' && (
                    <p className="text-sm text-gray-500 mt-1">
                      Trial ends: {new Date(clinic.trial_ends_at).toLocaleDateString()}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Authentication Settings Tab */}
        <TabsContent value="auth">
          <ClinicGoogleOAuthSettings />
        </TabsContent>

        {/* Billing Settings Tab */}
        <TabsContent value="billing">
          <div className="space-y-6">
            {/* Payment Gateways */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-teal-600" />
                  Payment Gateways
                </CardTitle>
                <CardDescription>
                  Configure your payment processing settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Stripe */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <span className="text-lg font-bold text-purple-600">S</span>
                      </div>
                      <div>
                        <h4 className="font-semibold">Stripe</h4>
                        <p className="text-sm text-gray-500">Accept credit cards and digital wallets</p>
                      </div>
                    </div>
                    <Switch
                      checked={billingSettings.stripe?.enabled || false}
                      onCheckedChange={(checked) => setBillingSettings(prev => ({
                        ...prev,
                        stripe: { ...prev.stripe, enabled: checked }
                      }))}
                    />
                  </div>
                  {billingSettings.stripe?.enabled && (
                    <div className="grid gap-4 pt-4 border-t">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Publishable Key</Label>
                          <Input
                            type="password"
                            value={billingSettings.stripe?.publishable_key || ''}
                            onChange={(e) => setBillingSettings(prev => ({
                              ...prev,
                              stripe: { ...prev.stripe, publishable_key: e.target.value }
                            }))}
                            placeholder="pk_test_..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Secret Key</Label>
                          <Input
                            type="password"
                            value={billingSettings.stripe?.secret_key || ''}
                            onChange={(e) => setBillingSettings(prev => ({
                              ...prev,
                              stripe: { ...prev.stripe, secret_key: e.target.value }
                            }))}
                            placeholder="sk_test_..."
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={billingSettings.stripe?.test_mode || false}
                          onCheckedChange={(checked) => setBillingSettings(prev => ({
                            ...prev,
                            stripe: { ...prev.stripe, test_mode: checked }
                          }))}
                        />
                        <Label>Test Mode</Label>
                      </div>
                      <Button
                        onClick={async () => {
                          setSaving(true);
                          try {
                            await axios.put(`${API}/clinic/billing-settings/stripe`, billingSettings.stripe);
                            toast.success('Stripe settings saved!');
                            fetchBillingSettings();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to save Stripe settings');
                          } finally {
                            setSaving(false);
                          }
                        }}
                        disabled={saving}
                        className="w-fit"
                      >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Stripe Settings
                      </Button>
                    </div>
                  )}
                </div>

                {/* PayPal */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="text-lg font-bold text-blue-600">P</span>
                      </div>
                      <div>
                        <h4 className="font-semibold">PayPal</h4>
                        <p className="text-sm text-gray-500">Accept PayPal payments</p>
                      </div>
                    </div>
                    <Switch
                      checked={billingSettings.paypal?.enabled || false}
                      onCheckedChange={(checked) => setBillingSettings(prev => ({
                        ...prev,
                        paypal: { ...prev.paypal, enabled: checked }
                      }))}
                    />
                  </div>
                  {billingSettings.paypal?.enabled && (
                    <div className="grid gap-4 pt-4 border-t">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Client ID</Label>
                          <Input
                            type="password"
                            value={billingSettings.paypal?.client_id || ''}
                            onChange={(e) => setBillingSettings(prev => ({
                              ...prev,
                              paypal: { ...prev.paypal, client_id: e.target.value }
                            }))}
                            placeholder="Enter PayPal Client ID"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Client Secret</Label>
                          <Input
                            type="password"
                            value={billingSettings.paypal?.client_secret || ''}
                            onChange={(e) => setBillingSettings(prev => ({
                              ...prev,
                              paypal: { ...prev.paypal, client_secret: e.target.value }
                            }))}
                            placeholder="Enter PayPal Client Secret"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={billingSettings.paypal?.sandbox_mode || false}
                          onCheckedChange={(checked) => setBillingSettings(prev => ({
                            ...prev,
                            paypal: { ...prev.paypal, sandbox_mode: checked }
                          }))}
                        />
                        <Label>Sandbox Mode</Label>
                      </div>
                      <Button
                        onClick={async () => {
                          setSaving(true);
                          try {
                            await axios.put(`${API}/clinic/billing-settings/paypal`, billingSettings.paypal);
                            toast.success('PayPal settings saved!');
                            fetchBillingSettings();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to save PayPal settings');
                          } finally {
                            setSaving(false);
                          }
                        }}
                        disabled={saving}
                        className="w-fit"
                      >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Save PayPal Settings
                      </Button>
                    </div>
                  )}
                </div>

                {/* Square */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-lg font-bold text-gray-600">□</span>
                      </div>
                      <div>
                        <h4 className="font-semibold">Square</h4>
                        <p className="text-sm text-gray-500">Accept Square payments</p>
                      </div>
                    </div>
                    <Switch
                      checked={billingSettings.square?.enabled || false}
                      onCheckedChange={(checked) => setBillingSettings(prev => ({
                        ...prev,
                        square: { ...prev.square, enabled: checked }
                      }))}
                    />
                  </div>
                  {billingSettings.square?.enabled && (
                    <div className="grid gap-4 pt-4 border-t">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Application ID</Label>
                          <Input
                            type="password"
                            value={billingSettings.square?.application_id || ''}
                            onChange={(e) => setBillingSettings(prev => ({
                              ...prev,
                              square: { ...prev.square, application_id: e.target.value }
                            }))}
                            placeholder="Enter Square App ID"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Access Token</Label>
                          <Input
                            type="password"
                            value={billingSettings.square?.access_token || ''}
                            onChange={(e) => setBillingSettings(prev => ({
                              ...prev,
                              square: { ...prev.square, access_token: e.target.value }
                            }))}
                            placeholder="Enter Square Access Token"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Location ID</Label>
                        <Input
                          value={billingSettings.square?.location_id || ''}
                          onChange={(e) => setBillingSettings(prev => ({
                            ...prev,
                            square: { ...prev.square, location_id: e.target.value }
                          }))}
                          placeholder="Enter Square Location ID"
                        />
                      </div>
                      <Button
                        onClick={async () => {
                          setSaving(true);
                          try {
                            await axios.put(`${API}/clinic/billing-settings/square`, billingSettings.square);
                            toast.success('Square settings saved!');
                            fetchBillingSettings();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to save Square settings');
                          } finally {
                            setSaving(false);
                          }
                        }}
                        disabled={saving}
                        className="w-fit"
                      >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Square Settings
                      </Button>
                    </div>
                  )}
                </div>

                {/* Clover */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <span className="text-lg font-bold text-green-600">C</span>
                      </div>
                      <div>
                        <h4 className="font-semibold">Clover</h4>
                        <p className="text-sm text-gray-500">Accept payments via Clover POS</p>
                      </div>
                    </div>
                    <Switch
                      checked={billingSettings.clover?.enabled || false}
                      onCheckedChange={(checked) => setBillingSettings(prev => ({
                        ...prev,
                        clover: { ...prev.clover, enabled: checked }
                      }))}
                    />
                  </div>
                  {billingSettings.clover?.enabled && (
                    <div className="grid gap-4 pt-4 border-t">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Merchant ID <span className="text-red-500">*</span></Label>
                          <Input
                            value={billingSettings.clover?.merchant_id || ''}
                            onChange={(e) => setBillingSettings(prev => ({
                              ...prev,
                              clover: { ...prev.clover, merchant_id: e.target.value }
                            }))}
                            placeholder="Your Clover Merchant ID"
                          />
                          <p className="text-xs text-gray-500">Find this in Clover Dashboard → Account & Setup</p>
                        </div>
                        <div className="space-y-2">
                          <Label>API Token <span className="text-red-500">*</span></Label>
                          <Input
                            type="password"
                            value={billingSettings.clover?.api_token || ''}
                            onChange={(e) => setBillingSettings(prev => ({
                              ...prev,
                              clover: { ...prev.clover, api_token: e.target.value }
                            }))}
                            placeholder="Your Clover API Token"
                          />
                          <p className="text-xs text-gray-500">Generate in Clover Dashboard → API Tokens</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <Button
                          variant="outline"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const res = await axios.post(`${API}/clover/test-connection`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (res.data.status === 'connected') {
                                toast.success(`Connected to ${res.data.merchant_name}`);
                              } else {
                                toast.error(res.data.message || 'Connection failed');
                              }
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Connection test failed');
                            }
                          }}
                          disabled={!billingSettings.clover?.merchant_id || !billingSettings.clover?.api_token}
                        >
                          <Zap className="h-4 w-4 mr-2" />
                          Test Connection
                        </Button>
                        <Button
                          onClick={async () => {
                            setSaving(true);
                            try {
                              const token = localStorage.getItem('token');
                              await axios.put(`${API}/clover/config`, billingSettings.clover, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success('Clover settings saved!');
                              fetchBillingSettings();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to save Clover settings');
                            } finally {
                              setSaving(false);
                            }
                          }}
                          disabled={saving || !billingSettings.clover?.merchant_id || !billingSettings.clover?.api_token}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                          Save Clover Settings
                        </Button>
                      </div>
                      <div className="text-sm text-gray-500 flex gap-4">
                        <a href="https://docs.clover.com/docs/generate-a-test-api-token" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> How to get API Token
                        </a>
                        <a href="https://sandbox.dev.clover.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> Sandbox Environment
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Payment & Invoice Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-teal-600" />
                  Payment & Invoice Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Input
                      value={billingSettings.payment?.currency || 'USD'}
                      onChange={(e) => setBillingSettings(prev => ({
                        ...prev,
                        payment: { ...prev.payment, currency: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tax Rate (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={billingSettings.payment?.tax_rate || 0}
                      onChange={(e) => setBillingSettings(prev => ({
                        ...prev,
                        payment: { ...prev.payment, tax_rate: parseFloat(e.target.value) || 0 }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Invoice Prefix</Label>
                    <Input
                      value={billingSettings.invoice?.invoice_prefix || 'INV-'}
                      onChange={(e) => setBillingSettings(prev => ({
                        ...prev,
                        invoice: { ...prev.invoice, invoice_prefix: e.target.value }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Terms (days)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="90"
                      value={billingSettings.invoice?.payment_terms_days || 30}
                      onChange={(e) => setBillingSettings(prev => ({
                        ...prev,
                        invoice: { ...prev.invoice, payment_terms_days: parseInt(e.target.value) || 30 }
                      }))}
                    />
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    setSaving(true);
                    try {
                      await axios.put(`${API}/clinic/billing-settings/payment`, billingSettings.payment);
                      await axios.put(`${API}/clinic/billing-settings/invoice`, billingSettings.invoice);
                      toast.success('Payment settings saved!');
                      fetchBillingSettings();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to save payment settings');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Payment Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Email Settings Tab */}
        <TabsContent value="email">
          <div className="space-y-6">
            {/* Email Provider */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-teal-600" />
                  Email Configuration
                </CardTitle>
                <CardDescription>
                  Configure how your clinic sends emails to patients
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* SendGrid */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Send className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">SendGrid Configuration</h4>
                        <p className="text-sm text-gray-500">
                          {hasCustomEmailDomain 
                            ? 'Send emails from your own domain' 
                            : 'Emails sent via MyMedMember (upgrade for custom domain)'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {emailSettings.is_verified && hasCustomEmailDomain && (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                      {!hasCustomEmailDomain && (
                        <Badge className="bg-amber-100 text-amber-700">
                          Enterprise Feature
                        </Badge>
                      )}
                      {hasCustomEmailDomain && (
                        <Switch
                          checked={emailSettings.sendgrid?.enabled || false}
                          onCheckedChange={(checked) => setEmailSettings(prev => ({
                            ...prev,
                            sendgrid: { ...prev.sendgrid, enabled: checked }
                          }))}
                        />
                      )}
                    </div>
                  </div>
                  
                  {/* Upgrade Message for non-Enterprise users */}
                  {!hasCustomEmailDomain && (
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 mt-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Shield className="h-5 w-5 text-amber-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-amber-800">Custom Email Domain - Enterprise Feature</h4>
                          <p className="text-sm text-amber-700 mt-1">
                            Send emails from your own domain (e.g., <code className="bg-amber-100 px-1 rounded">noreply@yourclinic.com</code>) 
                            instead of MyMedMember's default.
                          </p>
                          <ul className="text-sm text-amber-700 mt-2 space-y-1">
                            <li>✓ Better brand recognition</li>
                            <li>✓ Higher deliverability rates</li>
                            <li>✓ Professional appearance</li>
                          </ul>
                          <p className="text-xs text-amber-600 mt-3">
                            Currently, all emails are sent as: <strong>"{clinic?.name || 'Your Clinic'} via MyMedMember"</strong>
                          </p>
                          <Button 
                            variant="outline" 
                            className="mt-3 border-amber-300 text-amber-700 hover:bg-amber-100"
                            onClick={() => window.location.href = '/admin/billing'}
                          >
                            Upgrade to Enterprise
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {hasCustomEmailDomain && emailSettings.sendgrid?.enabled && (
                    <div className="grid gap-4 pt-4 border-t">
                      <div className="space-y-2">
                        <Label>SendGrid API Key</Label>
                        <Input
                          type="password"
                          value={emailSettings.sendgrid?.api_key || ''}
                          onChange={(e) => setEmailSettings(prev => ({
                            ...prev,
                            sendgrid: { ...prev.sendgrid, api_key: e.target.value }
                          }))}
                          placeholder="SG.xxxxxxxx..."
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>From Email</Label>
                          <Input
                            type="email"
                            value={emailSettings.sendgrid?.from_email || ''}
                            onChange={(e) => setEmailSettings(prev => ({
                              ...prev,
                              sendgrid: { ...prev.sendgrid, from_email: e.target.value }
                            }))}
                            placeholder="noreply@yourclinic.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>From Name</Label>
                          <Input
                            value={emailSettings.sendgrid?.from_name || ''}
                            onChange={(e) => setEmailSettings(prev => ({
                              ...prev,
                              sendgrid: { ...prev.sendgrid, from_name: e.target.value }
                            }))}
                            placeholder="Your Clinic Name"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={async () => {
                            setSaving(true);
                            try {
                              await axios.put(`${API}/clinic/email-settings/sendgrid`, emailSettings.sendgrid);
                              toast.success('SendGrid settings saved!');
                              fetchEmailSettings();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to save SendGrid settings');
                            } finally {
                              setSaving(false);
                            }
                          }}
                          disabled={saving}
                        >
                          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                          Save SendGrid Settings
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Test Email */}
                <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Test Email Configuration
                  </h4>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="Enter email to send test"
                      className="flex-1"
                    />
                    <Button
                      onClick={async () => {
                        if (!testEmail) {
                          toast.error('Please enter an email address');
                          return;
                        }
                        setSendingTest(true);
                        try {
                          await axios.post(`${API}/clinic/email-settings/test`, { to_email: testEmail });
                          toast.success('Test email sent successfully!');
                          fetchEmailSettings();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to send test email');
                        } finally {
                          setSendingTest(false);
                        }
                      }}
                      disabled={sendingTest || !hasCustomEmailDomain || !emailSettings.sendgrid?.enabled}
                    >
                      {sendingTest ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Send Test
                    </Button>
                  </div>
                  {!hasCustomEmailDomain && (
                    <p className="text-xs text-gray-500">
                      Test email requires Custom Email Domain feature (Enterprise tier)
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Notification Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-teal-600" />
                  Email Notifications
                </CardTitle>
                <CardDescription>
                  Choose which emails to send automatically
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Appointment Reminders</Label>
                      <p className="text-sm text-gray-500">Send reminders before appointments</p>
                    </div>
                    <Switch
                      checked={emailSettings.notifications?.send_appointment_reminders || false}
                      onCheckedChange={(checked) => setEmailSettings(prev => ({
                        ...prev,
                        notifications: { ...prev.notifications, send_appointment_reminders: checked }
                      }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Membership Confirmations</Label>
                      <p className="text-sm text-gray-500">Send confirmation when membership is created</p>
                    </div>
                    <Switch
                      checked={emailSettings.notifications?.send_membership_confirmations || false}
                      onCheckedChange={(checked) => setEmailSettings(prev => ({
                        ...prev,
                        notifications: { ...prev.notifications, send_membership_confirmations: checked }
                      }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Payment Receipts</Label>
                      <p className="text-sm text-gray-500">Send receipts after payments</p>
                    </div>
                    <Switch
                      checked={emailSettings.notifications?.send_payment_receipts || false}
                      onCheckedChange={(checked) => setEmailSettings(prev => ({
                        ...prev,
                        notifications: { ...prev.notifications, send_payment_receipts: checked }
                      }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Welcome Emails</Label>
                      <p className="text-sm text-gray-500">Send welcome email to new patients</p>
                    </div>
                    <Switch
                      checked={emailSettings.notifications?.send_welcome_emails || false}
                      onCheckedChange={(checked) => setEmailSettings(prev => ({
                        ...prev,
                        notifications: { ...prev.notifications, send_welcome_emails: checked }
                      }))}
                    />
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    setSaving(true);
                    try {
                      await axios.put(`${API}/clinic/email-settings/notifications`, emailSettings.notifications);
                      toast.success('Notification settings saved!');
                      fetchEmailSettings();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to save notification settings');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Notification Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* WordPress Integration Tab */}
        <TabsContent value="wordpress">
          <Card>
            <CardHeader>
              <CardTitle>WordPress Integration</CardTitle>
              <CardDescription>
                Connect your WordPress website
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>WordPress API Key</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={clinic.wordpress_api_key || 'Not generated'}
                    disabled
                    className="flex-1 font-mono text-sm"
                    type="password"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(clinic.wordpress_api_key)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Use this API key to connect your WordPress plugin
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">
                  WordPress Plugin Setup
                </h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Download the MyMedMember WordPress plugin</li>
                  <li>Install and activate it on your WordPress site</li>
                  <li>Enter your API key in the plugin settings</li>
                  <li>Configure the membership display shortcode</li>
                </ol>
                <Button
                  className="mt-4"
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.href = '/admin/wordpress'}
                >
                  Go to WordPress Tab
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Embed Widget Tab */}
        <TabsContent value="embed">
          <EmbedCodeGenerator clinic={clinic} />
        </TabsContent>

        {/* API & Integrations Tab */}
        <TabsContent value="integrations">
          <Card>
            <CardHeader>
              <CardTitle>API Keys & Integrations</CardTitle>
              <CardDescription>
                Configure third-party service integrations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={async (e) => {
                e.preventDefault();
                setSaving(true);
                try {
                  await axios.put(`${API}/clinic/info`, {
                    settings: {
                      ...clinic.settings,
                      google_maps_api_key: formData.google_maps_api_key
                    }
                  });
                  toast.success('API settings saved successfully!');
                  fetchClinicData();
                } catch (error) {
                  console.error('Failed to save API settings:', error);
                  toast.error('Failed to save API settings');
                } finally {
                  setSaving(false);
                }
              }} className="space-y-6">
                
                {/* Google Maps API Key */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Globe className="h-5 w-5 text-teal-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Google Maps API</h3>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="google_maps_api_key">Google Maps API Key</Label>
                    <Input
                      id="google_maps_api_key"
                      name="google_maps_api_key"
                      type="password"
                      placeholder="Enter your Google Maps API key"
                      value={formData.google_maps_api_key}
                      onChange={handleChange}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500">
                      Used for address autocomplete in forms. Get your API key from{' '}
                      <a 
                        href="https://console.cloud.google.com/google/maps-apis" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:underline"
                      >
                        Google Cloud Console
                      </a>
                    </p>
                  </div>

                  {formData.google_maps_api_key ? (
                    <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-900">API Key Configured</p>
                        <p className="text-xs text-green-700 mt-1">
                          Address autocomplete will use Google Maps Places API
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-900">No API Key Set</p>
                        <p className="text-xs text-amber-700 mt-1">
                          Address fields will use basic text input. Add an API key to enable autocomplete.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Setup Instructions</h4>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                      <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                      <li>Create a new project or select existing one</li>
                      <li>Enable "Places API" and "Maps JavaScript API"</li>
                      <li>Create credentials → API Key</li>
                      <li>Restrict the API key to your domain for security</li>
                      <li>Copy and paste the API key above</li>
                    </ol>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save API Settings
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Settings Tab */}
        <TabsContent value="notifications">
          <NotificationSettings />
        </TabsContent>

        {/* Patient Portal Links Tab */}
        <TabsContent value="patient-portal">
          <PatientPortalLinks clinic={clinic} />
        </TabsContent>

        {/* Patient ID Settings Tab */}
        <TabsContent value="patient-id">
          <PatientIdSettings />
        </TabsContent>

        {/* Payment Methods Settings Tab */}
        <TabsContent value="payment-methods">
          <PaymentMethodsSettings />
        </TabsContent>

        {/* Patient View / Impersonation Settings Tab */}
        <TabsContent value="impersonation">
          <ImpersonationSettings />
        </TabsContent>

        <TabsContent value="data-management">
          <DataManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClinicSettingsPage;
